// Keep background transfers bounded, with two reserved slots for newly visible work.
export default class ImageQueue {
  constructor() {
    this.jobs = new Map();
    this.invalidations = new Map();
    this.pending = [];
    this.active = new Set();
    this.timers = new Set();
    this.destroyed = false;
    this.order = 0;
  }

  request(spec, priority = 2, owner = null) {
    if (this.destroyed || !spec?.src) return Promise.resolve(null);
    const key = JSON.stringify(spec) + (spec.srcset ? `|${window.innerWidth}:${window.devicePixelRatio || 1}` : "");
    const existing = this.jobs.get(key);
    if (existing) {
      existing.consumers.set(owner, Math.min(existing.consumers.get(owner) ?? Infinity, priority));
      existing.priority = Math.min(...existing.consumers.values());
      if (existing.img && priority === 0) existing.img.setAttribute("fetchpriority", "high");
      this.drain();
      return existing.promise;
    }
    const job = { key, spec, priority, consumers: new Map([[owner, priority]]), order: this.order++, attempts: 0, cacheVersion: this.invalidations.get(key) || 0 };
    job.promise = new Promise((resolve) => { job.resolve = resolve; });
    this.jobs.set(key, job);
    this.pending.push(job);
    this.drain();
    return job.promise;
  }

  drain() {
    if (this.destroyed || this.canceling) return;
    this.pending.sort((a, b) => a.priority - b.priority || a.order - b.order);
    while (this.pending.length) {
      const job = this.pending[0];
      const limit = job.priority === 0 ? 6 : 4;
      if (this.active.size >= limit) break;
      this.pending.shift();
      this.start(job);
    }
  }

  start(job) {
    const picture = document.createElement("picture");
    picture.style.display = "none";
    picture.setAttribute("aria-hidden", "true");
    picture.dataset.imagePreloader = "";
    const img = new Image();
    job.img = img;
    job.picture = picture;
    this.active.add(job);
    let settled = false;
    let timeout;
    const finish = (url) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      img.onload = null;
      img.onerror = null;
      if (!url) {
        picture.querySelectorAll("source").forEach((source) => source.removeAttribute("srcset"));
        img.removeAttribute("srcset");
        img.removeAttribute("src");
      }
      picture.remove();
      job.img = null;
      job.picture = null;
      this.active.delete(job);
      if (url || this.destroyed || job.canceled || job.attempts >= 2) {
        job.result = url;
        if (!url) this.jobs.delete(job.key);
        job.resolve(url);
      } else {
        job.attempts += 1;
        const timer = window.setTimeout(() => {
          this.timers.delete(timer);
          job.timer = null;
          if (this.destroyed) return;
          this.pending.push(job);
          this.drain();
        }, job.attempts * 500);
        this.timers.add(timer);
        job.timer = timer;
      }
      this.drain();
    };
    job.cancel = () => finish(null);
    const retryUrl = (src) => {
      if (!job.attempts && !job.cacheVersion) return src;
      const url = new URL(src, window.location.href);
      if (job.attempts) url.searchParams.set("image-retry", String(job.attempts));
      if (job.cacheVersion) url.searchParams.set("image-recovery", String(job.cacheVersion));
      return url.href;
    };
    const retrySet = (srcset) => srcset.replace(/(\S+)(\s+\d+w)/g, (_match, src, width) => `${retryUrl(src)}${width}`);
    // Let the browser choose its supported format and exact DPR/viewport candidate.
    const sources = (job.spec.sources || []).slice(job.attempts);
    sources.forEach(({ type, srcset }) => {
      const source = document.createElement("source");
      source.type = type;
      source.srcset = retrySet(srcset);
      source.sizes = job.spec.sizes || "100vw";
      picture.appendChild(source);
    });
    img.decoding = "async";
    img.loading = "eager";
    img.setAttribute("fetchpriority", job.priority === 0 ? "high" : "low");
    img.sizes = job.spec.sizes || "100vw";
    img.onload = () => {
      const url = img.currentSrc || img.src;
      const decoded = typeof img.decode === "function" ? img.decode() : Promise.resolve();
      decoded.then(() => finish(url), () => finish(img.naturalWidth ? url : null));
    };
    img.onerror = () => finish(null);
    picture.appendChild(img);
    // Construct the complete picture before connecting it to avoid fallback downloads.
    if (job.spec.srcset) img.srcset = retrySet(job.spec.srcset);
    img.src = retryUrl(job.spec.src);
    document.body.appendChild(picture);
    timeout = window.setTimeout(() => {
      picture.querySelectorAll("source").forEach((source) => source.removeAttribute("srcset"));
      img.removeAttribute("srcset");
      img.removeAttribute("src");
      finish(null);
    }, 90000);
  }

  cancelOwner(owner) {
    this.canceling = true;
    this.jobs.forEach((job) => {
      if (!job.consumers.delete(owner)) return;
      if (job.consumers.size) {
        job.priority = Math.min(...job.consumers.values());
        if (job.img) job.img.setAttribute("fetchpriority", job.priority === 0 ? "high" : "low");
        return;
      }
      if (job.result) return;
      job.canceled = true;
      if (job.timer) {
        window.clearTimeout(job.timer);
        this.timers.delete(job.timer);
      }
      this.pending = this.pending.filter((item) => item !== job);
      if (this.active.has(job)) job.cancel();
      else {
        this.jobs.delete(job.key);
        job.resolve(null);
      }
    });
    this.canceling = false;
    this.drain();
  }

  invalidate(url) {
    this.jobs.forEach((job, key) => {
      if (job.result === url) {
        this.jobs.delete(key);
        this.invalidations.set(key, (this.invalidations.get(key) || 0) + 1);
      }
    });
  }

  destroy() {
    this.destroyed = true;
    this.timers.forEach((timer) => window.clearTimeout(timer));
    this.timers.clear();
    this.active.forEach((job) => job.cancel());
    this.jobs.forEach((job) => job.resolve(null));
    this.pending = [];
    this.jobs.clear();
    this.invalidations.clear();
  }
}
